import React, { useRef, useMemo, useState } from 'react';
import './index.scss';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import { ErrorMessage, Field, Formik, FormikProps } from 'formik';
import lodashFirst from 'lodash/first';
import { AppState } from '$/store';
import { CompanyService } from '$/services';
import { importCompanies } from '$/store/company/actions';
import BixButton from '$/components/global/bixButton';
import Modal from '$/components/global/modal';

type ParsedImportErrors = {
  rowErrors: Array<{ rowNumber: string; errors: string[] }>;
  fileDuplicates: Array<{ bixbyId: string; rows: string }>;
  dbConflicts: Array<{ row: string; bixbyId: string }>;
  generic: string[];
};

const ROW_VALIDATION_RE = /^Row (\d+) is invalid\. Errors: (.+?)\. Row data/;
const FILE_DUPLICATE_RE = /BixbyId '(.+?)' appears on rows: (.+)/;
const DB_CONFLICT_RE = /Row (\d+): BixbyId '(.+?)'/;

const pickString = (...values: any[]): string | undefined =>
  values.find(v => typeof v === 'string' && v.trim().length > 0);

const extractFromBody = (body: any): string | undefined => {
  if (typeof body === 'string' && body.trim().length > 0) return body;
  if (!body || typeof body !== 'object') return undefined;
  const fromFields = pickString(
    body.detail,
    body.message,
    body.title,
    body.error,
    body.errorMessage,
    body.exceptionMessage
  );
  if (fromFields) return fromFields;
  if (body.errors) return (Object.values(body.errors) as string[][]).flat().find(v => typeof v === 'string');
  return undefined;
};

const extractRawMessage = (err: any): string => {
  const fromBody = extractFromBody(err?.response?.data);
  if (fromBody) return fromBody;

  // BixbyError shape: err.response = { message, title, status } (no .data)
  if (err?.response && !err.response.data) {
    const fromResponse = pickString(err.response.detail, err.response.message, err.response.title);
    if (fromResponse) return fromResponse;
  }

  return 'An unexpected error occurred during import.';
};

const parseImportError = (rawData: any): ParsedImportErrors => {
  const message = extractRawMessage(rawData);
  const lines = message.split(/\r?\n/).filter(l => l.trim().length > 0);

  const result: ParsedImportErrors = { rowErrors: [], fileDuplicates: [], dbConflicts: [], generic: [] };

  for (const line of lines) {
    const rowMatch = ROW_VALIDATION_RE.exec(line);
    if (rowMatch) {
      result.rowErrors.push({ rowNumber: rowMatch[1], errors: rowMatch[2].split('; ') });
      continue;
    }

    if (line.includes('Duplicate BixbyId values found in the file')) {
      const detailPart = line.replace('Duplicate BixbyId values found in the file. ', '').replace(/\.$/, '');
      detailPart.split('; ').forEach(part => {
        const m = FILE_DUPLICATE_RE.exec(part);
        result.fileDuplicates.push(m ? { bixbyId: m[1], rows: m[2] } : { bixbyId: part, rows: '' });
      });
      continue;
    }

    if (line.includes('already exist in the database')) {
      const detailPart = line
        .replace('The following BixbyId values already exist in the database. ', '')
        .replace(/\.$/, '');
      detailPart.split('; ').forEach(part => {
        const m = DB_CONFLICT_RE.exec(part);
        result.dbConflicts.push(m ? { row: m[1], bixbyId: m[2] } : { row: '', bixbyId: part });
      });
      continue;
    }

    result.generic.push(line);
  }

  if (
    result.rowErrors.length === 0 &&
    result.fileDuplicates.length === 0 &&
    result.dbConflicts.length === 0 &&
    result.generic.length === 0
  )
    result.generic.push('An unexpected error occurred during import.');

  return result;
};

const renderParsedError = (parsed: ParsedImportErrors) => (
  <>
    {parsed.rowErrors.length > 0 && (
      <>
        <p className="import-error-panel__subtitle">The following rows have validation errors:</p>
        {parsed.rowErrors.map(re => (
          <div key={re.rowNumber}>
            <p className="import-error-panel__subtitle">
              <strong>Row {re.rowNumber}:</strong>
            </p>
            <ul className="import-error-panel__list">
              {re.errors.map(err => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </div>
        ))}
      </>
    )}
    {parsed.fileDuplicates.length > 0 && (
      <>
        <p className="import-error-panel__subtitle">
          Duplicate <strong>BixbyId</strong> values detected in the file:
        </p>
        <ul className="import-error-panel__list">
          {parsed.fileDuplicates.map(d => (
            <li key={d.bixbyId}>
              <strong>{d.bixbyId}</strong> — rows: {d.rows}
            </li>
          ))}
        </ul>
      </>
    )}
    {parsed.dbConflicts.length > 0 && (
      <>
        <p className="import-error-panel__subtitle">
          The following <strong>BixbyId</strong> values already exist in the database:
        </p>
        <ul className="import-error-panel__list">
          {parsed.dbConflicts.map(c => (
            <li key={`${c.row}-${c.bixbyId}`}>
              Row <strong>{c.row}</strong> — BixbyId: <strong>{c.bixbyId}</strong>
            </li>
          ))}
        </ul>
      </>
    )}
    {parsed.generic.map(msg => (
      <p key={msg} className="import-error-panel__subtitle">
        {msg}
      </p>
    ))}
  </>
);

interface IBulkImportCompanies {
  active: boolean;
  onImportSubmit: () => void;
  onCloseModal: () => void;
  dispatchImportCompanies: (companiesFile: File) => Promise<void>;
}

interface CompanyImportValues {
  companiesFile: File | null;
}

const BulkImportCompanies: React.FC<IBulkImportCompanies> = props => {
  const { active, onImportSubmit, onCloseModal, dispatchImportCompanies } = props;

  const [isImporting, setIsImporting] = useState(false);
  const [importErrorMessage, setImportErrorMessage] = useState<any>();

  const parsedError = useMemo<ParsedImportErrors | null>(
    () => (importErrorMessage === undefined ? null : parseImportError(importErrorMessage)),
    [importErrorMessage]
  );

  const formikRef = useRef<FormikProps<CompanyImportValues>>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleCloseModal = () => {
    setImportErrorMessage(undefined);
    setIsImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (formikRef.current) formikRef.current.resetForm();
    onCloseModal();
  };

  const importCompaniesAsync = async (values: CompanyImportValues) => {
    if (!values.companiesFile) return;

    try {
      setImportErrorMessage(undefined);
      setIsImporting(true);

      await dispatchImportCompanies(values.companiesFile);

      if (formikRef.current) formikRef.current.resetForm();
      if (fileInputRef.current) fileInputRef.current.value = '';

      onImportSubmit();
    } catch (err: any) {
      setImportErrorMessage(err);
    } finally {
      setIsImporting(false);
    }
  };

  const getTemplateFile = async () => {
    await CompanyService.getImportTemplate();
  };

  const getExtension = (fileName: string) => {
    return fileName.substring(fileName.lastIndexOf('.') + 1);
  };

  const schema = Yup.object({
    companiesFile: Yup.mixed()
      .required('File is required')
      .test({
        message: 'Please provide a supported file type (.xlsx)',
        test: (file, context) => {
          const isValid = getExtension((file as File).name) === 'xlsx';
          if (!isValid) context?.createError();
          return isValid;
        },
      }),
  });

  const initialValues: CompanyImportValues = {
    companiesFile: null,
  };

  return (
    <Modal title="Bulk Import Companies" active={active} forForm onClose={handleCloseModal}>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={async (values: CompanyImportValues) => {
          await importCompaniesAsync(values);
        }}>
        {({ handleSubmit }) => (
          <form className="import-company-form" onSubmit={handleSubmit}>
            <div className="search_block">
              <Field
                name="companiesFile"
                render={({ form }: { [key: string]: any }) => {
                  const handleFileChange = (ev: React.FormEvent<HTMLInputElement>) => {
                    const file = lodashFirst(ev.currentTarget.files);
                    form.setFieldValue('companiesFile', file);
                  };
                  return (
                    <>
                      <div className="search_block_title">{'Import File'}</div>
                      <input
                        ref={fileInputRef}
                        name="companiesFile"
                        type="file"
                        disabled={isImporting}
                        onChange={handleFileChange}
                      />
                      <button type="button" className="template_link" onClick={getTemplateFile}>
                        Download Import Template.xlsx
                      </button>
                    </>
                  );
                }}
              />
            </div>
            <ErrorMessage name="companiesFile">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
            <div className="btn_container">
              <BixButton useLoading isLoading={isImporting} disabled={isImporting} type="submit">
                Import Companies
              </BixButton>
            </div>
            {!!parsedError && (
              <div className="import-error-panel">
                <div className="import-error-panel__header">
                  <span className="import-error-panel__icon">!</span>
                  <span className="import-error-panel__title">Import Failed</span>
                </div>
                <div className="import-error-panel__body">{renderParsedError(parsedError)}</div>
                <div className="import-error-panel__footer">
                  <BixButton type="button" onClick={handleCloseModal}>
                    Close
                  </BixButton>
                </div>
              </div>
            )}
          </form>
        )}
      </Formik>
    </Modal>
  );
};

const mapStateToProps = (_state: AppState) => ({});

const mapDispatchToProps = (dispatch: any) => ({
  dispatchImportCompanies: (companiesFile: File) => dispatch(importCompanies(companiesFile)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkImportCompanies);
